<template>
  <sc-flex :is-column="true">
    <WarningDialog
      :opened.sync="pageOutOfRangeDialogOpened"
      warning-title="Page out of range"
      :warning-text="
        `
        The page number(s) you entered is out of range. Please enter a valid page number.\n
        Invalid page number(s) you entered: ${outOfRangePages.join(', ')}
      `
      "
      @close="pageOutOfRangeDialogOpened = false"
    />

    <WarningDialog
      :opened.sync="noPageClassifiedDialogOpened"
      warning-title="No Page Was Classified"
      warning-text="Please classify the pages using the auto classification tool or classify manually."
      @close="noPageClassifiedDialogOpened = false"
    />

    <ImagePreview
      :opened.sync="imagePreviewDialogOpened"
      :src="imagePreviewSrc"
    />

    <ConfirmationDialog
      v-model="autoClassifyConfirmationDialogOpened"
      @confirm="autoClassify"
      type="info"
    >
      <b>Please be advised that this feature makes simple mistakes</b>. While
      the accuracy will improve over time, you will need to review and adjust
      every pages classification. Are you sure you want to continue?
    </ConfirmationDialog>

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status === 'action_required'" type="info">
      <b>Instruction:</b> Please review the pages and add the correct pages
      under their respective classification on the right sidebar.
      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="classify"
          icon-right="arrow-right"
          :is-loading="submitting"
          :is-disabled="pdfLoading || autoClassifying"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>
    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :loading="submitting"
      :disabled="pdfLoading"
      @next="next"
    />

    <sc-flex>
      <aside class="sidebar hidden-sm-and-down" v-if="bookmarks.length > 0">
        <v-card>
          <v-card-title class="headline">Index</v-card-title>
          <v-flex class="caption ma-2">
            The index is generated from the PDF's bookmarks and the auto
            classification tool. You can click on a page to navigate to it.
          </v-flex>
          <v-list dense expand class="pb-0 pt-0">
            <template v-for="(bookmark, index) in bookmarks">
              <v-list-group
                v-if="bookmark.items && bookmark.items.length > 0"
                :key="bookmark.title + ' ' + index"
                :id="`bookmark-${bookmark.pageIndex}`"
              >
                <template v-slot:activator>
                  <v-list-tile
                    :style="
                      pageWithinBookmark(bookmark)
                        ? 'background-color: var(--blue-100)'
                        : ''
                    "
                  >
                    <v-list-tile-content>
                      <v-list-tile-title style="text-decoration: underline">{{
                        bookmark.title
                      }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </template>
                <v-list
                  dense
                  expand
                  class="pl-0 ml-3 text-align-left pt-0 pb-0"
                >
                  <template v-for="(subItem, subIndex) in bookmark.items">
                    <v-list-group
                      v-if="subItem.items && subItem.items.length > 0"
                      :key="subItem.title + ' ' + subIndex"
                    >
                      <template v-slot:activator>
                        <v-list-tile>
                          <v-list-tile-content>
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <span>{{ subItem.title }}</span>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                      <v-list
                        dense
                        expand
                        class="pl-0 ml-3 text-align-left pt-0 pb-0"
                      >
                        <template
                          v-for="(thirdItem, thirdIndex) in subItem.items"
                        >
                          <v-list-group
                            v-if="thirdItem.items && thirdItem.items.length > 0"
                            :key="thirdItem.title + ' ' + thirdIndex"
                          >
                            <template v-slot:activator>
                              <v-list-tile>
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <span>{{ thirdItem.title }}</span>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>
                            <v-list
                              dense
                              expand
                              class="pl-0 ml-3 text-align-left pt-0 pb-0"
                            >
                              <template
                                v-for="(fourthItem,
                                fourthIndex) in thirdItem.items"
                              >
                                <v-list-group
                                  v-if="
                                    fourthItem.items &&
                                      fourthItem.items.length > 0
                                  "
                                  :key="fourthItem.title + ' ' + fourthIndex"
                                >
                                  <template v-slot:activator>
                                    <v-list-tile>
                                      <v-list-tile-content>
                                        <v-list-tile-title>
                                          <v-layout
                                            justify-space-between
                                            align-center
                                          >
                                            <span>{{ fourthItem.title }}</span>
                                          </v-layout>
                                        </v-list-tile-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                  <v-list-tile
                                    v-for="fifthItem in fourthItem.items"
                                    :key="
                                      fifthItem.title +
                                        ' ' +
                                        fifthItem.pageIndex
                                    "
                                    @click="goToPage(fifthItem.pageIndex)"
                                    :style="
                                      selectedThumbnail === fifthItem.pageIndex
                                        ? 'background-color: var(--blue-100)'
                                        : {}
                                    "
                                    class="pl-4"
                                  >
                                    <v-list-tile-title>
                                      <v-layout
                                        justify-space-between
                                        align-center
                                      >
                                        <v-layout justify-start align-center>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('filing_instructions')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'filing_instructions',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('payment_vouchers')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'payment_vouchers',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('e_sign_forms')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'e_sign_forms',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('tax_return_pages')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'tax_return_pages',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('transmittal_letters')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'transmittal_letters',
                                              )}`
                                            "
                                          ></div>
                                          <div
                                            v-if="
                                              classificationForPage(
                                                fifthItem.pageIndex,
                                              ).includes('paper_filed_returns')
                                            "
                                            :style="
                                              `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                                'paper_filed_returns',
                                              )}`
                                            "
                                          ></div>
                                          <span style="max-width: 250px;">{{
                                            fifthItem.title
                                          }}</span>
                                        </v-layout>
                                      </v-layout>
                                    </v-list-tile-title>
                                  </v-list-tile>
                                </v-list-group>
                                <v-list-tile
                                  v-else-if="fourthItem.pageIndex"
                                  :key="
                                    fourthItem.title +
                                      ' ' +
                                      fourthItem.pageIndex
                                  "
                                  @click="goToPage(fourthItem.pageIndex)"
                                  :style="
                                    selectedThumbnail === fourthItem.pageIndex
                                      ? 'background-color: var(--blue-100)'
                                      : {}
                                  "
                                  class="pl-4"
                                >
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <v-layout justify-start align-center>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('filing_instructions')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'filing_instructions',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('payment_vouchers')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'payment_vouchers',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('e_sign_forms')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'e_sign_forms',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('tax_return_pages')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'tax_return_pages',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('transmittal_letters')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'transmittal_letters',
                                            )}`
                                          "
                                        ></div>
                                        <div
                                          v-if="
                                            classificationForPage(
                                              fourthItem.pageIndex,
                                            ).includes('paper_filed_returns')
                                          "
                                          :style="
                                            `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                              'paper_filed_returns',
                                            )}`
                                          "
                                        ></div>
                                        <span style="max-width: 250px;">{{
                                          fourthItem.title
                                        }}</span>
                                      </v-layout>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile>
                              </template>
                            </v-list>
                          </v-list-group>
                          <v-list-tile
                            v-else-if="thirdItem.pageIndex"
                            :key="thirdItem.title + ' ' + thirdItem.pageIndex"
                            @click="goToPage(thirdItem.pageIndex)"
                            :style="
                              selectedThumbnail === thirdItem.pageIndex
                                ? 'background-color: var(--blue-100)'
                                : {}
                            "
                            class="pl-4"
                          >
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <v-layout justify-start align-center>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('filing_instructions')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'filing_instructions',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('payment_vouchers')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'payment_vouchers',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('e_sign_forms')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'e_sign_forms',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('tax_return_pages')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'tax_return_pages',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('transmittal_letters')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'transmittal_letters',
                                      )}`
                                    "
                                  ></div>
                                  <div
                                    v-if="
                                      classificationForPage(
                                        thirdItem.pageIndex,
                                      ).includes('paper_filed_returns')
                                    "
                                    :style="
                                      `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                        'paper_filed_returns',
                                      )}`
                                    "
                                  ></div>
                                  <span style="max-width: 250px;">{{
                                    thirdItem.title
                                  }}</span>
                                </v-layout>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile>
                        </template>
                      </v-list>
                    </v-list-group>
                    <v-list-tile
                      v-else-if="subItem.pageIndex"
                      :key="subItem.title + ' ' + subItem.pageIndex"
                      @click="goToPage(subItem.pageIndex)"
                      :style="
                        selectedThumbnail === subItem.pageIndex
                          ? 'background-color: var(--blue-100)'
                          : {}
                      "
                      class="pl-4"
                    >
                      <v-list-tile-title>
                        <v-layout justify-space-between align-center>
                          <v-layout justify-start align-center>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('filing_instructions')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'filing_instructions',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('payment_vouchers')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'payment_vouchers',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('e_sign_forms')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'e_sign_forms',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('tax_return_pages')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'tax_return_pages',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('transmittal_letters')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'transmittal_letters',
                                )}`
                              "
                            ></div>
                            <div
                              v-if="
                                classificationForPage(
                                  subItem.pageIndex,
                                ).includes('paper_filed_returns')
                              "
                              :style="
                                `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                                  'paper_filed_returns',
                                )}`
                              "
                            ></div>
                            <span style="max-width: 250px;">{{
                              subItem.title
                            }}</span>
                          </v-layout>
                        </v-layout>
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>
                </v-list>
              </v-list-group>
              <v-list-tile
                v-else-if="bookmark.pageIndex"
                :key="bookmark.title + ' ' + index + 'no items'"
                :id="`bookmark-${bookmark.pageIndex}`"
                @click="goToPage(bookmark.pageIndex)"
                :style="
                  selectedThumbnail === bookmark.pageIndex
                    ? 'background-color: var(--blue-100)'
                    : ''
                "
              >
                <v-list-tile-title>
                  <v-layout align-center>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'filing_instructions',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'filing_instructions',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'payment_vouchers',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'payment_vouchers',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'e_sign_forms',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'e_sign_forms',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'tax_return_pages',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'tax_return_pages',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'transmittal_letters',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'transmittal_letters',
                        )}`
                      "
                    ></div>
                    <div
                      v-if="
                        classificationForPage(bookmark.pageIndex).includes(
                          'paper_filed_returns',
                        )
                      "
                      :style="
                        `margin-right: 6px; height: 15px; width: 6px; border-radius: 4px; background-color: ${colorForSection(
                          'paper_filed_returns',
                        )}`
                      "
                    ></div>
                    <span
                      style="max-width: 250px;"
                      class="mr-2"
                      :style="
                        bookmark.title.startsWith('Page') ? 'width: 50px;' : ''
                      "
                      >{{ bookmark.title }}</span
                    >
                    <v-tooltip
                      bottom
                      v-if="
                        guessedPages.find(
                          page => page.page === bookmark.pageIndex.toString(),
                        ) && bookmark.title.startsWith('Page')
                      "
                    >
                      <v-chip
                        slot="activator"
                        small
                        class="my-0"
                        style="max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                        >{{
                          guessedPages.find(
                            page => page.page === bookmark.pageIndex.toString(),
                          ).form_name
                        }}</v-chip
                      >
                      <span
                        >AI generated form name:<br />{{
                          guessedPages.find(
                            page => page.page === bookmark.pageIndex.toString(),
                          ).form_name
                        }}</span
                      >
                    </v-tooltip>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile>
            </template>
          </v-list>
        </v-card>
      </aside>
      <v-layout column>
        <v-alert
          v-if="isSelectionModeOn"
          :value="isSelectionModeOn"
          outline
          type="info"
          class="ma-0 selection-banner py-2"
        >
          <v-layout align-center justify-space-between>
            <span class="body-1">
              You are currently in <b>Selection Mode</b>. Please click on the
              thumbnails to select pages for
              <b>{{
                selectedSection.hasSubSections
                  ? selectedSection.text + ` (${selectedSubSectionKey})`
                  : selectedSection.text
              }}</b
              >.
            </span>
            <v-btn
              color="primary"
              class="text-none pa-0 ma-0"
              @click="selectedSectionKey = null"
            >
              <b class="white--text">Done</b>
            </v-btn>
          </v-layout>
        </v-alert>

        <!-- Partial: Content -->
        <v-layout justify-center wrap class="pdf-preview">
          <v-layout
            justify-center
            align-center
            v-if="pdfLoading"
            class="loading-container"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-layout>

          <div v-else class="thumbnail-container" v-for="t in sortedThumbnails">
            <v-layout align-center>
              <sc-flex :gap="2" :is-y-centered="true">
                <sc-small>Page {{ t.page }}</sc-small>

                <sc-small>
                  <v-menu offset-y v-if="getPageSections(t.page).length === 0">
                    <v-chip
                      slot="activator"
                      small
                      color="grey"
                      text-color="white"
                    >
                      Unclassified
                      <v-icon color="white">arrow_drop_down</v-icon>
                    </v-chip>

                    <v-list class="select-section-list">
                      <v-layout
                        v-for="section in sections"
                        :key="section.key"
                        v-if="!section.hidden"
                      >
                        <v-layout v-if="section.hasSubSections" column>
                          <v-list-tile
                            v-for="subSectionKey in getSelectableSubSections(
                              section.key,
                            )"
                            :key="subSectionKey"
                            @click="
                              changePageSection(
                                t.page,
                                undefined,
                                section.key,
                                undefined,
                                subSectionKey,
                              )
                            "
                            :disabled="taxReturn.status !== 'action_required'"
                          >
                            <v-list-tile-title>{{
                              section.text + ` (${subSectionKey})`
                            }}</v-list-tile-title>
                          </v-list-tile>
                        </v-layout>

                        <v-list-tile
                          v-else
                          @click="
                            changePageSection(t.page, undefined, section.key)
                          "
                          :disabled="taxReturn.status !== 'action_required'"
                        >
                          <v-list-tile-title>{{
                            section.text
                          }}</v-list-tile-title>
                        </v-list-tile>
                      </v-layout>
                    </v-list>
                  </v-menu>

                  <v-menu
                    v-else
                    offset-y
                    v-for="{
                      section: selectedSection,
                      subSectionKey: selectedSubSectionKey,
                    } in getPageSections(t.page)"
                    :key="selectedSection.key"
                  >
                    <v-chip
                      slot="activator"
                      small
                      :color="selectedSection.color"
                      text-color="white"
                    >
                      {{
                        selectedSection.hasSubSections
                          ? selectedSection.text + ` (${selectedSubSectionKey})`
                          : selectedSection.text
                      }}
                      <v-icon color="white">arrow_drop_down</v-icon>
                    </v-chip>

                    <v-list class="select-section-list">
                      <v-layout
                        v-for="section in sections"
                        v-if="!section.hidden"
                        :key="section.key"
                      >
                        <v-layout v-if="section.hasSubSections" column>
                          <v-list-tile
                            v-for="subSectionKey in getSelectableSubSections(
                              section.key,
                            )"
                            :disabled="
                              (selectedSection.key === section.key &&
                                selectedSubSectionKey === subSectionKey) ||
                                taxReturn.status !== 'action_required'
                            "
                            @click="
                              changePageSection(
                                t.page,
                                selectedSection.key,
                                section.key,
                                selectedSubSectionKey,
                                subSectionKey,
                              )
                            "
                          >
                            <v-list-tile-action>
                              <v-icon
                                v-if="
                                  selectedSection.key === section.key &&
                                    selectedSubSectionKey === subSectionKey
                                "
                                color="success"
                                >check</v-icon
                              >
                            </v-list-tile-action>

                            <v-list-tile-content>
                              <v-list-tile-title>{{
                                section.text + ` (${subSectionKey})`
                              }}</v-list-tile-title>
                            </v-list-tile-content>
                          </v-list-tile>
                        </v-layout>

                        <v-list-tile
                          v-else
                          :disabled="taxReturn.status !== 'action_required'"
                          @click="
                            selectedSection.key === section.key
                              ? changeToUnclassify(
                                  t.page,
                                  selectedSection.key,
                                  selectedSubSectionKey,
                                )
                              : changePageSection(
                                  t.page,
                                  selectedSection.key,
                                  section.key,
                                  selectedSubSectionKey,
                                )
                          "
                        >
                          <v-list-tile-action>
                            <v-icon
                              v-if="selectedSection.key === section.key"
                              color="success"
                              >check</v-icon
                            >
                          </v-list-tile-action>

                          <v-list-tile-content>
                            <v-list-tile-title>{{
                              section.text
                            }}</v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </v-layout>
                    </v-list>
                  </v-menu>
                </sc-small>
              </sc-flex>

              <v-btn
                flat
                icon
                color="secondary"
                class="zoom-btn"
                @click="zoomIn(t)"
              >
                <v-icon>zoom_in</v-icon>
              </v-btn>
            </v-layout>

            <div
              class="image-container ph-no-capture"
              :style="
                selectedThumbnail === t.page
                  ? { border: '2px solid var(--blue-700)' }
                  : { border: '2px solid transparent' }
              "
              @click="selectThumbnail(t.page)"
            >
              <img
                :id="`thumbnail-page-${t.page}`"
                :src="t.thumbnail"
                class="thumbnail"
                @click="isSelectionModeOn ? toggleSelect(t.page) : null"
                :class="{
                  selected: selectedPages.includes(t.page),
                  selectable: isSelectionModeOn,
                  [`thumbnail-page-${t.page}`]: true,
                }"
              />

              <div
                v-if="watermarkedPages.includes(t.page)"
                class="watermark"
                :style="getWatermarkStyle(t.page)"
              >
                DO NOT FILE
              </div>
            </div>
          </div>
        </v-layout>
      </v-layout>

      <!-- Partial: Sidebar -->
      <aside class="sidebar">
        <v-layout column class="autoclassify">
          <v-tooltip bottom>
            <sc-button
              v-if="!autoClassifying"
              type="primary"
              :is-disabled="thumbnails.length === 0 || !editable"
              :is-loading="autoClassifying"
              @click="openAutoClassifyConfirmationDialog()"
              slot="activator"
              >Auto classify</sc-button
            >
            <span>
              Auto-classify uncategorized pages.
            </span>
          </v-tooltip>

          <sc-button
            v-if="autoClassifying"
            type="secondary"
            @click="cancelAutoClassify()"
            >Cancel</sc-button
          >
          <v-layout align-center justify-start class="pt-2">
            <v-checkbox
              v-model="showOnlyUncategorized"
              @change="flipFilters"
              color="primary"
              hide-details
              class="ma-0 pa-0"
              :label="
                `Show only uncategorized pages (${allPagesCount -
                  classifiedPagesCount})`
              "
            />
          </v-layout>

          <v-layout column class="pt-2" v-if="estimatedOcrTime">
            <div class="body-1">
              Please hang on tight while we process this tax return for
              auto-classification. You can exit and return later. The estimated
              wait time is
              <b class="red--text text--darken-2">
                {{
                  formatEstimatedTime(
                    estimatedOcrTime + estimatedClassificationTime,
                  )
                }}.
              </b>
            </div>
          </v-layout>

          <v-layout column class="pt-2" v-else-if="estimatedClassificationTime">
            <div v-if="autoClassifying" class="body-1">
              The estimated time to complete auto-classification is
              <b>{{ formatEstimatedTime(estimatedClassificationTime) }}.</b>
            </div>

            <div v-else class="body-1">
              This tax return is ready for auto-classification.
            </div>
          </v-layout>

          <v-layout v-if="autoClassifying" class="py-2">
            <v-progress-linear
              background-color="grey lighten-2"
              class="my-0"
              v-model="classificationProgress"
            />
          </v-layout>
        </v-layout>

        <div
          v-for="section in sections.filter(section => !section.hidden)"
          :key="section.key"
          class="sidebar__content"
        >
          <sc-flex :is-column="true" :gap="2">
            <v-layout align-center justify-start>
              <div
                :style="
                  `margin-right: 6px; height: 12px; width: 12px; border-radius: 4px; background-color: ${section.color}`
                "
              />
              <sc-h4>
                {{ section.text }}
              </sc-h4>
              <v-spacer />
              <v-tooltip bottom>
                <v-icon
                  icon
                  flat
                  :color="currentFilter === section.key ? 'primary' : 'grey'"
                  @click="toggleSectionVisibility(section.key)"
                  class="mr-1"
                  slot="activator"
                >
                  visibility
                </v-icon>
                <span>Show only {{ section.text }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <v-icon
                  class="ml-1 primary--text"
                  size="20"
                  @click="goToCategory(section.key)"
                  slot="activator"
                  >file_open</v-icon
                >
                <span>Go to next page in category</span>
              </v-tooltip>
            </v-layout>
            <sc-small>{{ section.description }}</sc-small>
          </sc-flex>

          <sc-flex
            :is-column="true"
            :gap="16"
            class="pt-2"
            v-if="typeof pageRangesBySection[section.key] === 'object'"
          >
            <sc-flex
              v-for="[subSectionKey, pageRange] in toPairs(
                pageRangesBySection[section.key],
              )"
              :key="subSectionKey"
              :is-column="true"
            >
              <v-layout justify-space-between align-center>
                <sc-h6>Pages for {{ subSectionKey }}:</sc-h6>

                <v-btn
                  icon
                  flat
                  small
                  color="error"
                  :disabled="!editable"
                  @click="deletePaperFiledReturnPackage(subSectionKey)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-layout>

              <input
                class="sidebar__input"
                :class="{
                  selected:
                    selectedSectionKey === section.key &&
                    selectedSubSectionKey === subSectionKey,
                }"
                :placeholder="pdfLoading ? 'Loading...' : 'Example: 1-5, 7'"
                :disabled="!editable"
                v-model="pageRangesBySection[section.key][subSectionKey]"
                @focus="selectSection(section.key, subSectionKey)"
              />
            </sc-flex>
          </sc-flex>

          <sc-flex :is-column="true" :gap="8" class="pt-2" v-else>
            <sc-h6>Pages:</sc-h6>
            <input
              class="sidebar__input"
              :class="{ selected: selectedSectionKey === section.key }"
              :placeholder="pdfLoading ? 'Loading...' : 'Example: 1-5, 7'"
              :disabled="!editable"
              v-model="pageRangesBySection[section.key]"
              @focus="selectSection(section.key)"
            />
          </sc-flex>

          <sc-flex v-if="section.key === 'tax_return_pages'" :is-column="true">
            <v-checkbox
              class="mt-1"
              hide-details
              v-model="taxReturn.watermark_enabled"
              color="primary"
              :disabled="!editable"
              label="Add 'DO NOT FILE' watermark"
              @click.native.stop.prevent=""
            />
          </sc-flex>

          <sc-flex
            v-if="section.key === 'paper_filed_returns'"
            :is-column="true"
          >
            <v-btn
              color="primary"
              flat
              class="text-none py-1"
              block
              @click.native.stop.prevent="addPaperFiledReturnPackage()"
              :disabled="!editable"
            >
              <v-icon size="medium">add</v-icon>
              Add packages
            </v-btn>
          </sc-flex>
        </div>
      </aside>
    </sc-flex>
  </sc-flex>
</template>

<script>
import { generatePdfThumbnails } from '@/utils/pdf';
import { PDFDocument } from 'pdf-lib';
import API from '@/api';
import uniq from 'lodash/uniq';
import { mapGetters } from 'vuex';
import rangeParser from 'parse-numeric-range';
import axios from 'axios';
import mapValues from 'lodash/mapValues';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import WarningDialog from '@/components/returns/WarningDialog';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import ImagePreview from '@/components/ImagePreview';
// import posthog from 'posthog-js';
import { v4 as uuid } from 'uuid';
import { Sema } from 'async-sema';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { duration } from 'moment';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    WarningDialog,
    ReadonlyAlert,
    ConfirmationDialog,
    ImagePreview,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      selectedThumbnail: null,
      bookmarks: [],
      pdfLoading: false,
      thumbnails: [],
      submitting: false,
      autoClassifying: false,
      autoClassifyConfirmationDialogOpened: false,

      classificationRequestId: null,

      sections: [],
      selectedSectionKey: null,
      selectedSubSectionKey: null,
      pageRangesBySection: {
        transmittal_letters: '',
        filing_instructions: '',
        tax_return_pages: '',
        e_sign_forms: '',
        paper_filed_returns: {},
        payment_vouchers: '',
      },

      imagePreviewDialogOpened: false,
      imagePreviewSrc: null,

      pageOutOfRangeDialogOpened: false,
      noPageClassifiedDialogOpened: false,

      estimatedOcrTime: null,
      getEstimatedWaitingTimeHandler: null,

      textPages: [],
      showOnlyUncategorized: false,
      currentFilter: 'none',
      guessedPages: [],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    isUsingCustomClassificationModel() {
      return (
        (this.currentCompany.entity_type === 'Individual' &&
          this.taxReturnIndividualCustomModelEnabled) ||
        (this.currentCompany.entity_type !== 'Individual' &&
          this.taxReturnBusinessCustomModelEnabled)
      );
    },

    estimatedClassificationTime() {
      if (this.isUsingCustomClassificationModel) {
        return null;
      }

      const throughPut = 8; // 8 images per second

      if (!this.taxReturn.original_images || this.estimatedOcrTime === null) {
        return 0;
      }

      const classifiedPages = this.classifiedPagesCount;
      const unclassifiedPages = this.allPagesCount - classifiedPages;

      const estimated = Math.ceil(unclassifiedPages / throughPut);

      return estimated;
    },

    classificationProgress() {
      return (this.classifiedPagesCount / this.allPagesCount) * 100;
    },

    editable() {
      return (
        this.taxReturn.status === 'action_required' &&
        !this.autoClassifying &&
        !this.submitting &&
        !this.pdfLoading
      );
    },

    pagesBySection() {
      return mapValues(this.pageRangesBySection, subSectionOrPageRanges => {
        if (typeof subSectionOrPageRanges === 'object') {
          return mapValues(subSectionOrPageRanges, pageRange =>
            rangeParser(pageRange),
          );
        } else {
          return rangeParser(subSectionOrPageRanges);
        }
      });
    },

    sortedThumbnails() {
      return this.thumbnails
        .sort((a, b) => a.page - b.page)
        .filter(thumbnail =>
          this.currentFilter !== 'none'
            ? this.classificationForPage(thumbnail.page).includes(
                this.currentFilter,
              )
            : !this.showOnlyUncategorized ||
              this.classificationForPage(thumbnail.page).length === 0,
        );
    },

    isSelectionModeOn() {
      return this.selectedSectionKey !== null;
    },

    selectedSection() {
      return this.sections.find(
        section => section.key === this.selectedSectionKey,
      );
    },

    selectedPages() {
      if (!this.selectedSection) {
        return [];
      }

      if (this.selectedSection.hasSubSections) {
        return this.pagesBySection[this.selectedSectionKey][
          this.selectedSubSectionKey
        ];
      } else {
        return this.pagesBySection[this.selectedSectionKey];
      }
    },

    watermarkedPages() {
      return this.taxReturn.watermark_enabled
        ? this.pagesBySection['tax_return_pages']
        : [];
    },

    outOfRangePages() {
      const maxPage = this.thumbnails.length;

      return uniq(
        Object.values(this.pagesBySection)
          .flatMap(pagesOrSubSections => {
            if (typeof pagesOrSubSections === 'object') {
              return Object.values(pagesOrSubSections).flat();
            } else {
              return pagesOrSubSections;
            }
          })
          .filter(page => {
            return page < 1 || page > maxPage;
          }),
      );
    },

    isNoneClassified() {
      return !Object.values(this.pagesBySection).some(pagesOrSubSections => {
        if (typeof pagesOrSubSections === 'object') {
          return Object.values(pagesOrSubSections).flat().length !== 0;
        } else {
          return pagesOrSubsections.length !== 0;
        }
      });
    },

    allPagesCount() {
      return this.thumbnails.length;
    },

    classifiedPagesCount() {
      const classifiedPages = Object.values(this.pagesBySection)
        .map(pagesOrSubSections => {
          if (typeof pagesOrSubSections === 'object') {
            return Object.values(pagesOrSubSections).flat();
          } else {
            return pagesOrSubsections;
          }
        })
        .flat();

      return uniq(classifiedPages).length;
    },
    bookmarksFlattened() {
      const flattenBookmarks = bookmarks => {
        let pages = [];
        bookmarks.forEach(bookmark => {
          if (bookmark.pageIndex) {
            pages.push(bookmark.pageIndex);
          }
          if (bookmark.items && bookmark.items.length > 0) {
            pages = pages.concat(flattenBookmarks(bookmark.items));
          }
        });
        return pages;
      };
      return flattenBookmarks(this.bookmarks);
    },
  },

  methods: {
    isEmpty,
    toPairs,

    colorForSection(section) {
      return this.sections.find(s => s.key === section).color;
    },

    flipFilters() {
      this.selectedThumbnail = null;
      this.currentFilter = 'none';
      this.sections.forEach(section => {
        section.show_filter = !this.showOnlyUncategorized;
      });
    },

    toggleSectionVisibility(sectionKey) {
      if (this.currentFilter === sectionKey) {
        this.currentFilter = 'none';
        this.sections.forEach(section => {
          section.show_filter = true;
        });
      } else {
        this.currentFilter = sectionKey;
        this.sections.find(
          section => section.key === sectionKey,
        ).show_filter = true;

        this.sections.forEach(section => {
          if (section.key !== sectionKey) {
            section.show_filter = false;
          }
        });
      }
    },

    goToCategory(category) {
      const lastOneInCategory = Math.max(
        ...this.thumbnails
          .filter(thumbnail =>
            this.classificationForPage(thumbnail.page).includes(category),
          )
          .map(thumbnail => thumbnail.page),
      );
      const page =
        lastOneInCategory === this.selectedThumbnail ||
        !this.classificationForPage(this.selectedThumbnail).includes(category)
          ? this.thumbnails.find(thumbnail =>
              this.classificationForPage(thumbnail.page).includes(category),
            )
          : this.thumbnails.find(
              thumbnail =>
                this.classificationForPage(thumbnail.page).includes(category) &&
                this.selectedThumbnail < thumbnail.page,
            );

      if (page) {
        this.goToPage(page.page);
      }
    },

    selectThumbnail(page) {
      this.selectedThumbnail = page;
      this.scrollToBookmark(page);
    },

    scrollToBookmark(page) {
      const pageElement = document.querySelector(`#bookmark-${page + 2}`);
      if (pageElement) {
        // scroll so the page is in the center of the screen
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    formatEstimatedTime(seconds) {
      const units = [
        { label: 'hour', value: 3600 },
        { label: 'minute', value: 60 },
        { label: 'second', value: 1 },
      ];

      const time = [];

      units.forEach(unit => {
        const unitValue = Math.floor(seconds / unit.value);
        if (unitValue > 0) {
          time.push(`${unitValue} ${unit.label}${unitValue > 1 ? 's' : ''}`);
          seconds %= unit.value;
        }
      });

      return time.join(', ');
    },

    async loadThumbnails() {
      let thumbnails;

      try {
        // If there are no original images, then load thumbnails from the original PDF
        if (this.taxReturn.original_images) {
          const response = await API.taxReturn.getOriginalImages({
            id: this.taxReturn.id,
            companyId: this.currentCompany.id,
          });

          thumbnails = response.data.map(image => {
            return {
              page: image.page,
              thumbnail: image.url,
            };
          });
        }
      } catch (error) {
        console.error({ error });
      }

      const response1 = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });
      const pdfSrc = response1.data.document.data.link;
      this.extractBookmarks(pdfSrc);
      if (!thumbnails || thumbnails.length === 0) {
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });

        thumbnails = await generatePdfThumbnails(pdfData, 1000);
      }
      this.thumbnails = thumbnails;
    },

    async classify() {
      if (this.outOfRangePages.length > 0) {
        this.pageOutOfRangeDialogOpened = true;
        return;
      } else if (this.isNoneClassified) {
        this.noPageClassifiedDialogOpened = true;
        return;
      }

      try {
        this.submitting = true;

        const response = await axios.post(
          `/tax_returns/${this.taxReturn.id}/classify`,
          {
            company_id: this.currentCompany.id,
            pages_by_section: this.pagesBySection,
            watermark_enabled: this.taxReturn.watermark_enabled,
          },
        );

        this.$emit('classification-updated', response.data);
      } finally {
        this.submitting = false;
      }
    },

    selectSection(sectionKey, subSectionKey) {
      if (!this.editable) {
        return;
      }

      this.selectedSectionKey = sectionKey;
      this.selectedSubSectionKey = subSectionKey || null;
    },

    toggleSelect(page) {
      if (!this.isSelectionModeOn) {
        return;
      }

      let newSelectedPages = [];

      if (this.selectedPages.includes(page)) {
        newSelectedPages = this.selectedPages.filter(p => p !== page);
      } else {
        newSelectedPages = [...this.selectedPages, page];
      }

      // Handle the case for paper-filed returns that has sub-sections
      if (this.selectedSubSectionKey !== null) {
        this.$set(this.pageRangesBySection, this.selectedSectionKey, {
          ...this.pageRangesBySection[this.selectedSectionKey],

          [this.selectedSubSectionKey]: this.convertToRanges(
            newSelectedPages,
          ).join(', '),
        });
      } else {
        this.$set(
          this.pageRangesBySection,
          this.selectedSectionKey,
          this.convertToRanges(newSelectedPages).join(', '),
        );
      }
    },

    zoomIn(image) {
      this.imagePreviewSrc = image.thumbnail;
      this.imagePreviewDialogOpened = true;
    },

    next() {
      this.$emit('next');
    },

    getPageSections(page) {
      const items = [];

      this.sections.forEach(section => {
        const subSectionOrPageRanges = this.pageRangesBySection[section.key];

        // The case when the section has sub sections (like Paper-filed returns)
        if (typeof subSectionOrPageRanges === 'object') {
          toPairs(subSectionOrPageRanges).forEach(
            ([subSectionKey, pageRange]) => {
              if (rangeParser(pageRange).includes(page)) {
                items.push({
                  section,
                  subSectionKey,
                });
              }
            },
          );
        } else if (rangeParser(subSectionOrPageRanges).includes(page)) {
          items.push({ section });
        }
      });

      return items;
    },

    getSelectableSubSections(sectionKey) {
      const subSections = this.pageRangesBySection[sectionKey];

      return Object.keys(subSections);
    },

    setPageRangesFromSections(sections) {
      this.pageRangesBySection.transmittal_letters = Array.isArray(
        sections.transmittal_letters,
      )
        ? this.convertToRanges(sections.transmittal_letters).join(', ')
        : '';

      this.pageRangesBySection.filing_instructions = Array.isArray(
        sections.filing_instructions,
      )
        ? this.convertToRanges(sections.filing_instructions).join(', ')
        : '';

      this.pageRangesBySection.tax_return_pages = Array.isArray(
        sections.tax_return_pages,
      )
        ? this.convertToRanges(sections.tax_return_pages).join(', ')
        : '';

      this.pageRangesBySection.e_sign_forms = Array.isArray(
        sections.e_sign_forms,
      )
        ? this.convertToRanges(sections.e_sign_forms).join(', ')
        : '';

      this.pageRangesBySection.payment_vouchers = Array.isArray(
        sections.payment_vouchers,
      )
        ? this.convertToRanges(sections.payment_vouchers).join(', ')
        : '';

      this.pageRangesBySection.paper_filed_returns =
        typeof sections.paper_filed_returns === 'object'
          ? mapValues(sections.paper_filed_returns, pages =>
              this.convertToRanges(pages).join(', '),
            )
          : {};
    },

    isPageClassified(page) {
      return Object.values(this.pagesBySection).some(pagesOrSubSections => {
        if (typeof pagesOrSubSections === 'object') {
          return Object.values(pagesOrSubSections)
            .flat()
            .includes(page);
        } else {
          return pagesOrSubsections.includes(page);
        }
      });
    },

    convertToRanges(array) {
      const uniqSorted = uniq(array.sort((a, b) => a - b));
      const ranges = [];

      for (let i = 0; i < uniqSorted.length; i++) {
        let rangeStart = uniqSorted[i];
        let rangeEnd = uniqSorted[i];

        while (uniqSorted[i + 1] - uniqSorted[i] === 1) {
          rangeEnd = uniqSorted[i + 1];
          i++;
        }

        if (rangeStart === rangeEnd) ranges.push(rangeStart);
        else ranges.push(`${rangeStart}-${rangeEnd}`);
      }

      return ranges;
    },

    convertRangesToArray(rangeString) {
      if (!rangeString) return [];

      return rangeString.split(',').flatMap(range => {
        const [start, end] = range.split('-').map(Number);
        return end
          ? Array.from({ length: end - start + 1 }, (_, i) => start + i)
          : [start];
      });
    },

    async extractBookmarks(pdfSrc) {
      try {
        const pdflib = window.pdfjsLib;
        pdflib.GlobalWorkerOptions.workerSrc =
          'https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.mjs';

        const loadingTask = pdflib.getDocument({ url: pdfSrc });
        const pdf = await loadingTask.promise; // Store pdf.js document instance

        this.extractBookmarksFromPdf(pdf);
      } catch (error) {
        console.error('Error extracting bookmarks:', error);
      }
    },

    async extractBookmarksFromPdf(pdf) {
      try {
        const outline = await pdf.getOutline();
        const numPages = pdf.numPages;

        if (!outline) {
          this.bookmarks = [];
        } else {
          const processBookmarks = async bookmarks => {
            const processed = await Promise.all(
              bookmarks.map(async bookmark => {
                let pageIndex = null;

                // Extract page index correctly from dest[0]
                if (
                  bookmark.dest &&
                  bookmark.dest.length > 0 &&
                  bookmark.dest[0]
                ) {
                  try {
                    const resolvedPageIndex = await pdf.getPageIndex(
                      bookmark.dest[0],
                    );
                    pageIndex = resolvedPageIndex + 1; // Convert to 1-based index
                  } catch (error) {
                    console.warn(
                      `Failed to resolve page index for: ${bookmark.title}`,
                      error,
                    );
                  }
                }

                return {
                  title: bookmark.title,
                  pageIndex: pageIndex,
                  items: bookmark.items
                    ? await processBookmarks(bookmark.items)
                    : [],
                };
              }),
            );

            return processed;
          };

          this.bookmarks = await processBookmarks(outline);

          if (this.bookmarks.length === 1) {
            this.bookmarks = this.bookmarks[0].items;
          }
        }

        this.insertSkippedPagesIntoBookmarks();

        this.textPages = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map(
              item => item.str,
              // `${item.str} ${Math.round(item.width)} ${item.transform
              //   .map(t => Math.round(t))
              //   .join(',')}`,
            )
            .join(' ');
          const bookmark = this.bookmarks.find(
            bookmark => bookmark.pageIndex === pageNum,
          );
          this.textPages.push({
            page: pageNum,
            text: pageText,
            bookmark: bookmark ? bookmark.title : null,
          });
        }
        console.log('this.textPages', this.textPages);
      } catch (error) {
        console.error('Error processing bookmarks:', error);
      }
    },

    pageWithinBookmark(bookmark) {
      return bookmark.items.some(
        item => item.pageIndex === this.selectedThumbnail,
      );
    },

    goToPage(pageIndex) {
      const pageElement = document.querySelector(
        `.thumbnail-page-${pageIndex}`,
      );
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.selectedThumbnail = pageIndex;
      }
    },

    insertSkippedPagesIntoBookmarks() {
      console.log('insertSkippedPagesIntoBookmarks');
      let page = 1;
      if (this.bookmarks.length > 0) {
        const initialBookmarkedPage = this.bookmarks
          .filter(bookmark => bookmark.pageIndex)
          .map(bookmark => bookmark.pageIndex)[0];

        while (initialBookmarkedPage > page) {
          this.bookmarks.splice(page - 1, 0, {
            pageIndex: page,
            title: `Page ${page}`,
            items: [],
          });
          page++;
        }

        while (page <= this.thumbnails.length) {
          if (!this.bookmarksFlattened.includes(page)) {
            const bookmark = this.bookmarks.find(
              bookmark => bookmark.pageIndex > page,
            );
            if (bookmark) {
              this.bookmarks.splice(bookmark.pageIndex, 0, {
                pageIndex: page,
                title: `Page ${page}`,
                items: [],
              });
            } else {
              this.bookmarks.push({
                pageIndex: page,
                title: `Page ${page}`,
                items: [],
              });
            }
          }
          page++;
        }
      } else {
        while (page <= this.thumbnails.length) {
          this.bookmarks.push({
            pageIndex: page,
            title: `Page ${page}`,
            items: [],
          });
          page++;
        }
      }
    },

    async autoClassify() {
      this.autoClassifyConfirmationDialogOpened = false;
      this.autoClassifying = true;
      this.selectedSectionKey = null;

      const requestId = uuid();
      this.classificationRequestId = requestId;

      const pageIndices = this.thumbnails.map((_, index) => index);

      const unclassifiedPageIndices = pageIndices.filter(
        index => !this.isPageClassified(index + 1),
      );

      const sema = new Sema(4); // Limit concurrency of classify tasks at a time

      // Create the classify tasks to be run in parallel
      const classifyTasks = unclassifiedPageIndices.map(async pageIndex => {
        const page = pageIndex + 1;

        await sema.acquire();

        try {
          if (requestId === this.classificationRequestId) {
            await this.classifyPage(page, requestId);
          }
        } finally {
          sema.release();
        }
      });

      await Promise.all(classifyTasks);

      if (requestId === this.classificationRequestId) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Auto classification completed.',
        });
        this.autoClassifying = false;

        // posthog.capture('auto classified pages', {
        //   company_id: this.currentCompany.id,
        // });
      }
    },

    async classifyPage(page, requestId) {
      try {
        if (this.isPageClassified(page)) {
          return;
        }
        const MAX_TEXT_LENGTH = 10000; // Adjust as needed

        let pageDetails = [];

        if (this.taxReturnUseNativePdfReaderForClassificationEnabled) {
          pageDetails = this.textPages.find(textPage => textPage.page === page);

          // Truncate the text if it's too long
          if (pageDetails && pageDetails.text.length > MAX_TEXT_LENGTH) {
            pageDetails.text =
              pageDetails.text.slice(0, MAX_TEXT_LENGTH) + '...'; // Append "..." to indicate truncation
          }
        }

        // Implement retry logic with a 1-minute timeout
        let response;
        let retries = 0;
        const maxRetries = 30; // 1 minute with 2-second intervals

        while (retries < maxRetries) {
          try {
            response = await axios.get(
              `/tax_returns/${this.taxReturn.id}/get_suggested_form_types`,
              {
                params: {
                  pages: [page], // Limited pages
                  page_details: pageDetails,
                  company_id: this.currentCompany.id,
                },
              },
            );
            break; // Success, exit the retry loop
          } catch (error) {
            // Only retry if we get a 425 Too Early response
            if (error.response && error.response.status === 425) {
              console.log('Got 425 Too Early response, retrying...');
              retries++;
              if (retries >= maxRetries) {
                console.error(
                  'Failed to get suggested form types after multiple retries:',
                  error,
                );
                Sentry.captureException(error);
                throw error; // Rethrow after max retries
              }
              // Wait 2 seconds before retrying
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }

            // For any other error, don't retry
            console.error('Error getting suggested form types:', error);
            Sentry.captureException(error);
            throw error;
          }
        }

        if (requestId === this.classificationRequestId) {
          const newPagesBySection = { ...this.pagesBySection };

          response.data.forEach(result => {
            if (!this.isPageClassified(result.page)) {
              if (result.form_type === 'filing-instructions') {
                newPagesBySection.filing_instructions = newPagesBySection.filing_instructions.concat(
                  [result.page],
                );
              } else if (result.form_type === 'e-sign-form') {
                newPagesBySection.e_sign_forms = newPagesBySection.e_sign_forms.concat(
                  [result.page],
                );
              } else if (result.form_type === 'payment-voucher') {
                newPagesBySection.payment_vouchers = newPagesBySection.payment_vouchers.concat(
                  [result.page],
                );
              } else if (result.form_type === 'tax-return-page') {
                newPagesBySection.tax_return_pages = newPagesBySection.tax_return_pages.concat(
                  [result.page],
                );
              }
            }
            if (result.form_name) {
              this.guessedPages.push({
                page: result.page,
                form_name: result.form_name,
              });
            }
          });

          const newlyClassifiedPages = response.data.map(result => result.page);

          // If the page was not classified, add it to the tax_return_pages section
          if (
            !newlyClassifiedPages.includes(page) &&
            !this.isPageClassified(page) &&
            !this.taxReturnUseNativePdfReaderForClassificationEnabled
          ) {
            newPagesBySection.tax_return_pages = newPagesBySection.tax_return_pages.concat(
              [page],
            );
          }

          this.setPageRangesFromSections(newPagesBySection);
        }
      } catch (error) {
        console.error('Error classifying page:', error);
        Sentry.captureException(error);
      }
    },

    cancelAutoClassify() {
      this.classificationRequestId = null;
      this.autoClassifying = false;
    },

    getWatermarkStyle(page) {
      const imageEl = document.querySelector(`.thumbnail-page-${page}`);

      if (!imageEl) {
        return;
      }

      // Calculate font size based on image container dimensions
      // 0.1 means 10% of the image width
      const fontSize = imageEl.offsetWidth * 0.1;

      return {
        fontSize: fontSize + 'px',
      };
    },

    changeToUnclassify(page, oldSectionKey, oldSubSectionKey) {
      // Remove page from sub-section (such as Paper-filed returns)
      if (typeof oldSubSectionKey !== 'undefined') {
        const currentPages =
          this.pagesBySection[oldSectionKey][oldSubSectionKey] || [];
        const newPages = currentPages.filter(p => p !== page);

        this.$set(this.pageRangesBySection, oldSectionKey, {
          ...this.pageRangesBySection[oldSectionKey],
          [oldSubSectionKey]: this.convertToRanges(newPages).join(', '),
        });
      } else {
        // Remove page from the old section
        this.$set(
          this.pageRangesBySection,
          oldSectionKey,
          this.convertToRanges(
            this.pagesBySection[oldSectionKey].filter(p => p !== page),
          ).join(', '),
        );
      }
    },

    changePageSection(
      page,
      oldSectionKey,
      newSectionKey,
      oldSubSectionKey,
      newSubSectionKey,
    ) {
      // Add page to sub-section (such as Paper-filed returns)
      if (typeof newSubSectionKey !== 'undefined') {
        const currentPages =
          this.pagesBySection[newSectionKey][newSubSectionKey] || [];

        const newPages = currentPages.concat([page]);

        this.$set(this.pageRangesBySection, newSectionKey, {
          ...this.pageRangesBySection[newSectionKey],
          [newSubSectionKey]: this.convertToRanges(newPages).join(', '),
        });
      }
      // Add page to normal section
      else {
        this.$set(
          this.pageRangesBySection,
          newSectionKey,
          this.convertToRanges(
            this.pagesBySection[newSectionKey].concat([page]),
          ).join(', '),
        );
      }

      if (oldSectionKey) {
        // Remove page from sub-section (such as Paper-filed returns)
        if (typeof oldSubSectionKey !== 'undefined') {
          const currentPages =
            this.pagesBySection[oldSectionKey][oldSubSectionKey] || [];
          const newPages = currentPages.filter(p => p !== page);

          this.$set(this.pageRangesBySection, oldSectionKey, {
            ...this.pageRangesBySection[oldSectionKey],
            [oldSubSectionKey]: this.convertToRanges(newPages).join(', '),
          });
        }
        // Remove page from normal section
        else {
          // Remove page from the old section
          this.$set(
            this.pageRangesBySection,
            oldSectionKey,
            this.convertToRanges(
              this.pagesBySection[oldSectionKey].filter(p => p !== page),
            ).join(', '),
          );
        }
      }
    },

    // async getEstimatedWaitingTime() {
    //   const response = await API.taxReturn.getEstimatedOcrTime({
    //     id: this.taxReturn.id,
    //     companyId: this.currentCompany.id,
    //   });

    //   this.estimatedOcrTime = response.data.estimated_ocr_time;
    // },

    // autoGetEstimatedWaitingTime() {
    //   this.getEstimatedWaitingTimeHandler = setInterval(
    //     this.getEstimatedWaitingTime,
    //     1000,
    //   );
    // },

    openAutoClassifyConfirmationDialog() {
      if (this.editable) {
        this.autoClassifyConfirmationDialogOpened = true;
      }
    },

    addPaperFiledReturnPackage() {
      let returnName = window.prompt(
        'Enter the name of the paper-filed return:',
      );

      if (isEmpty(returnName)) {
        return;
      }

      this.$set(this.pageRangesBySection.paper_filed_returns, returnName, '');
    },

    deletePaperFiledReturnPackage(returnName) {
      this.$set(this.pageRangesBySection, 'paper_filed_returns', {
        ...omit(this.pageRangesBySection.paper_filed_returns, returnName),
      });

      this.selectedSectionKey = null;
      this.selectedSubSectionKey = null;
    },

    classificationForPage(pageIndex) {
      let classification = [];
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.filing_instructions,
        ).includes(pageIndex)
      ) {
        classification.push('filing_instructions');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.payment_vouchers,
        ).includes(pageIndex)
      ) {
        classification.push('payment_vouchers');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.e_sign_forms,
        ).includes(pageIndex)
      ) {
        classification.push('e_sign_forms');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.tax_return_pages,
        ).includes(pageIndex)
      ) {
        classification.push('tax_return_pages');
      }
      if (
        this.convertRangesToArray(
          this.pageRangesBySection.transmittal_letters,
        ).includes(pageIndex)
      ) {
        classification.push('transmittal_letters');
      }

      if (typeof this.pageRangesBySection.paper_filed_returns === 'object') {
        for (const packageName in this.pageRangesBySection
          .paper_filed_returns) {
          const pages = this.convertRangesToArray(
            this.pageRangesBySection.paper_filed_returns[packageName],
          );

          if (pages.includes(pageIndex)) {
            classification.push(`paper_filed_returns`);
          }
        }
      }

      return classification;
    },
  },

  // async beforeDestroy() {
  //   if (this.getEstimatedWaitingTimeHandler) {
  //     clearInterval(this.getEstimatedWaitingTimeHandler);
  //   }
  // },

  async mounted() {
    this.pdfLoading = true;

    try {
      await this.loadThumbnails();

      this.setPageRangesFromSections({
        transmittal_letters: Array.isArray(this.taxReturn.transmittal_letters)
          ? this.taxReturn.transmittal_letters.map(form => form.page)
          : [],
        filing_instructions: Array.isArray(this.taxReturn.filing_instructions)
          ? this.taxReturn.filing_instructions.map(form => form.page)
          : [],
        tax_return_pages: Array.isArray(this.taxReturn.tax_return_pages)
          ? this.taxReturn.tax_return_pages.map(form => form.page)
          : [],
        e_sign_forms: Array.isArray(this.taxReturn.e_sign_forms)
          ? this.taxReturn.e_sign_forms.map(form => form.page)
          : [],
        payment_vouchers: Array.isArray(this.taxReturn.payment_vouchers)
          ? this.taxReturn.payment_vouchers
              .filter(form => form.page !== null)
              .map(form => form.page)
          : [],

        paper_filed_returns: Array.isArray(this.taxReturn.paper_filed_returns)
          ? fromPairs(
              this.taxReturn.paper_filed_returns
                .filter(paperFiledReturn => paperFiledReturn.pages)
                .map(paperFiledReturn => {
                  return [
                    paperFiledReturn.custom_return_name,
                    paperFiledReturn.pages.slice(),
                  ];
                }),
            )
          : {},
      });

      // if (!this.isUsingCustomClassificationModel) {
      //   this.autoGetEstimatedWaitingTime();
      // }
    } catch (error) {
      console.error({ error });
    } finally {
      this.pdfLoading = false;
    }
  },

  async created() {
    this.sections = [];
    this.guessedPages = [];

    this.sections.push({
      key: 'transmittal_letters',
      text: 'Transmittal Letters',
      hidden: true,
      description: '',
      color: '#000',
      show_filter: true,
    });

    this.sections.push({
      key: 'tax_return_pages',
      text: 'Tax Return Pages',
      description:
        'Enter the page numbers/ranges for this client to review before e-signing.',
      color: '#1e88e5',
      show_filter: true,
    });

    this.sections.push({
      key: 'e_sign_forms',
      text: 'E-Sign Forms',
      description:
        'Enter the page numbers/ranges for this client to e-sign after reviewing the Tax Return Pages.',
      color: '#43a047',
      show_filter: true,
    });

    if (this.taxReturnPaperFiledReturnsEnabled) {
      this.sections.push({
        key: 'paper_filed_returns',
        text: 'Paper-Filed Returns',
        description:
          'Add paper-filed return packages for this client to download, sign and mail them.',
        color: '#f57c00',
        hasSubSections: true,
        show_filter: true,
      });
    }

    this.sections.push({
      key: 'payment_vouchers',
      text: 'Payment Vouchers',
      description:
        'Enter the page numbers/ranges of all payment vouchers for this client.',
      color: '#6d4c41',
      show_filter: true,
    });

    this.sections.push({
      key: 'filing_instructions',
      text: 'Filing Instructions',
      optional: true,
      description: this.showFilingInstructionsToClients
        ? 'Enter the page numbers/ranges for the filing instructions. These pages will be viewable by clients as filing instruction. You can change this setting in firm settings under Preferences'
        : 'Enter the page numbers/ranges for the filing instructions. These pages will NOT be viewable by clients. You can change this setting in firm settings under Preferences',
      color: '#5e35b1',
      show_filter: true,
    });
  },
};
</script>

<style lang="postcss" scoped>
.pdf-preview {
  flex: auto;
  overflow-y: auto;

  height: 100%;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
  background-color: var(--gray-100);
}

@media (max-width: 1260px) {
  .pdf-preview {
    min-height: calc(100dvh - 157px);
    max-height: calc(100dvh - 157px);
  }
}

.selection-banner {
  box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.25);
}

.thumbnail-container {
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 24px;
}

.thumbnail {
  display: flex;
  width: 500px;
  max-width: 100%;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  position: relative;

  &.selectable {
    cursor: pointer;

    &:hover {
      border-color: var(--blue-500);
      box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
    }

    &.selected {
      border-width: 2px;
      border-color: var(--blue-700);
    }
  }
}

.image-container {
  position: relative;
  display: inline-block;
}

.watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
  color: rgba(0, 0, 0, 0.25);
  pointer-events: none; /* Allows clicks to pass through the watermark */
  white-space: nowrap;
}

.zoom-btn {
  margin-left: 16px;
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  min-width: 350px;
  max-width: 350px;
  overflow: scroll;
  padding: 0px 0px 64px;

  background-color: var(--gray-100);
  border-left: 1px solid var(--gray-300);
}

@media (max-width: 1260px) {
  .sidebar {
    min-height: calc(100vh - 157px);
    max-height: calc(100vh - 157px);
  }
}

.autoclassify {
  padding: 16px;
  background-color: #8eb2ff21;
}

.sidebar__content {
  display: flex;
  gap: 4px;
  padding: 20px 20px;
  flex-direction: column;
  border-bottom: 1px solid var(--gray-300);
}
.sidebar__content.selected {
  background-color: #e9f4fd;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
}

.sidebar__input {
  box-sizing: border-box;
  height: 40px;
  padding: 0 8px;
  width: 100%;

  background-color: var(--gray-0);
  border: 0;
  border-radius: 4px;
  outline: 1px solid var(--gray-200);

  font-family: var(--font-family-sans);
  line-height: 40px;
}
.sidebar__input.selected:not(:disabled) {
  box-shadow: 0px 1px 4px 1px rgba(0, 0, 0, 0.25);
  outline: 2px solid var(--blue-500);
}

.sidebar__input:disabled {
  background-color: var(--gray-100);

  pointer-events: none;
}

.select-section-list div[role='listitem'] {
  width: 100%;
}
</style>
